import { useMemo, useState } from 'react';

import { PlayerSearchSuggestionDto, SeasonEventDto, TeamByNameDto } from 'utils/api/dto';
import * as endpoints from 'utils/api/endpoints';
import { useDebouncedValue } from 'utils/hooks/use-debounced-value';
import { httpMethods } from 'utils/hooks/use-fetch-data';
import { UniversalFetchConfig, useUniversalFetchData } from 'utils/hooks/use-universal-fetch-data';

import { INPUT_TIMEOUT, MIN_INPUT_LENGTH } from '../constants';

export type UseSearchPlayerTeamsParams = {
    inputValue: string;
    isOnlyRecentPlayer?: boolean;
    isOnlyRecentTeam?: boolean;
    isOnlyEvents?: boolean;
};

type SearchKind = 'players' | 'teams' | 'events';

type SearchResults = {
    players: PlayerSearchSuggestionDto[];
    teams: TeamByNameDto[];
    events: SeasonEventDto[];
};

type FoundItem = PlayerSearchSuggestionDto | TeamByNameDto | SeasonEventDto;

const EMPTY_RESULTS: SearchResults = { players: [], teams: [], events: [] };

const SEARCH_URLS: Record<SearchKind, (query: string) => string> = {
    players: endpoints.scoutSearchPlayersByName,
    teams: endpoints.teamsClientSearch,
    events: endpoints.eventSearch
};

const getSearchKinds = ({
    isOnlyRecentPlayer,
    isOnlyRecentTeam,
    isOnlyEvents
}: Omit<UseSearchPlayerTeamsParams, 'inputValue'>): SearchKind[] => {
    if (isOnlyEvents) return ['events'];
    if (isOnlyRecentPlayer) return ['players'];
    if (isOnlyRecentTeam) return ['teams'];

    return ['players', 'teams'];
};

// As lodash sortBy did before: by the code points of the text, items without it last, the order of equal ones kept
const sortByText = <T>(items: T[], getText: (item: T) => string | null | undefined): T[] =>
    [...items].sort((a, b) => {
        const textA = getText(a) ?? null;
        const textB = getText(b) ?? null;
        if (textA === textB) return 0;
        if (textA === null) return 1;
        if (textB === null) return -1;

        return textA < textB ? -1 : 1;
    });

// The answers come in the order of the kinds; each kind is the DTO of its endpoint
const toResults = (kinds: SearchKind[], answers: Array<FoundItem[] | null>): SearchResults => {
    const answerOf = <T extends FoundItem>(kind: SearchKind) => (answers[kinds.indexOf(kind)] ?? []) as T[];

    return {
        players: sortByText(answerOf<PlayerSearchSuggestionDto>('players'), player => player.lastNameEn),
        teams: sortByText(answerOf<TeamByNameDto>('teams'), team => team.nameEn),
        events: sortByText(answerOf<SeasonEventDto>('events'), event => event.name)
    };
};

/**
 * The search of players, teams or events by name while the user types: the trimmed query is searched when it has at
 * least MIN_INPUT_LENGTH characters and has not changed for INPUT_TIMEOUT ms; a newer query cancels the older
 * request (useUniversalFetchData requests a changed list again). processing is true from the first key press until the answer
 * for the current query comes, so an empty list always means "nothing found" (failed: a request failed, the answers of the others are shown).
 */
export const useSearchPlayerTeams = ({
    inputValue,
    isOnlyRecentPlayer,
    isOnlyRecentTeam,
    isOnlyEvents
}: UseSearchPlayerTeamsParams) => {
    const query = inputValue.trim();
    const searchQuery = useDebouncedValue({ value: query, delay: INPUT_TIMEOUT });
    const kinds = useMemo(() => getSearchKinds({ isOnlyRecentPlayer, isOnlyRecentTeam, isOnlyEvents }), [
        isOnlyRecentPlayer,
        isOnlyRecentTeam,
        isOnlyEvents
    ]);
    const searchKey = `${kinds.join('+')}:${searchQuery}`;
    // The key of the search whose answer the hook holds: set by the handlers of that very batch
    const [answeredKey, setAnsweredKey] = useState<string | null>(null);

    const requests = useMemo(
        (): UniversalFetchConfig<FoundItem[]>[] =>
            kinds.map(kind => ({
                method: httpMethods.get,
                url: SEARCH_URLS[kind](searchQuery),
                isDisabled: searchQuery.length < MIN_INPUT_LENGTH,
                responseHandler: () => setAnsweredKey(searchKey),
                onRequestError: () => setAnsweredKey(searchKey)
            })),
        [kinds, searchQuery, searchKey]
    );
    const { data, error, processing } = useUniversalFetchData<FoundItem[]>(requests);

    return useMemo(() => {
        const isAnswered =
            query.length >= MIN_INPUT_LENGTH && answeredKey === `${kinds.join('+')}:${query}` && !processing;
        const failed = isAnswered && error.some(Boolean);

        return {
            ...(isAnswered ? toResults(kinds, data) : EMPTY_RESULTS),
            processing: query.length >= MIN_INPUT_LENGTH && !isAnswered,
            failed
        };
    }, [query, kinds, answeredKey, processing, error, data]);
};
